import type { CartState, ProjectOptions } from './projection.ts';
import { cartsAtSite, compareEvents, projectCarts } from './projection.ts';
import type { ClientEvent } from './events.ts';
import type { Cart } from './catalog.ts';

/**
 * Round resolution.
 *
 * A round has no stored lifecycle to keep alive: it is an opened event, its member scans,
 * and a terminating event. "Is a round in progress?" is therefore a question answered by
 * reading the local log, which is why an app killed mid-sweep loses nothing.
 */

export interface RoundSummary {
  round_id: string;
  site_code: string;
  opened_at: string;
  user_id: string;
  device_id: string;
  /** A full tour of the site, or a cart recorded after the fact. See RoundOpened.mode. */
  mode: 'sweep' | 'correction';
  /** Carts counted by this round — retracted scans excluded. */
  scanned: string[];
  closed_at: string | null;
  abandoned_at: string | null;
  missing: string[];
  status: 'open' | 'closed' | 'abandoned';
  /** Times this round was taken up again after finishing. Shown, never hidden. */
  reopen_count: number;
  last_reopened_at: string | null;
  /** Ids of `cart_scanned` events withdrawn from this round. */
  retracted_scan_ids: string[];
}

export function summariseRounds(events: readonly ClientEvent[]): Map<string, RoundSummary> {
  const ordered = [...events].sort(compareEvents);
  const rounds = new Map<string, RoundSummary>();

  // Gathered first, because a retraction can legitimately arrive before the scan it
  // withdraws when pages of a sync land out of order.
  const retracted = new Set<string>();
  for (const e of ordered) if (e.type === 'scan_retracted') retracted.add(e.scan_id);

  for (const e of ordered) {
    if (e.type === 'round_opened') {
      rounds.set(e.round_id, {
        round_id: e.round_id,
        site_code: e.site_code,
        opened_at: e.at,
        user_id: e.user_id,
        device_id: e.device_id,
        mode: e.mode ?? 'sweep',
        scanned: [],
        closed_at: null,
        abandoned_at: null,
        missing: [],
        status: 'open',
        reopen_count: 0,
        last_reopened_at: null,
        retracted_scan_ids: [],
      });
      continue;
    }

    const r = rounds.get(e.round_id);
    // A scan can legitimately arrive before its opening event when pages of a sync land out
    // of order, so tolerate the gap rather than dropping the fact.
    if (!r) continue;

    if (e.type === 'cart_scanned') {
      if (retracted.has(e.id)) continue;
      if (!r.scanned.includes(e.cart_code)) r.scanned.push(e.cart_code);
    } else if (e.type === 'scan_retracted') {
      r.retracted_scan_ids.push(e.scan_id);
    } else if (e.type === 'round_closed') {
      r.closed_at = e.at;
      r.missing = e.missing.filter((c) => !r.scanned.includes(c));
      r.status = 'closed';
    } else if (e.type === 'round_abandoned') {
      r.abandoned_at = e.at;
      r.status = 'abandoned';
    } else if (e.type === 'round_reopened') {
      // The conclusion is withdrawn, not amended: the next closure states it afresh.
      r.closed_at = null;
      r.abandoned_at = null;
      r.missing = [];
      r.status = 'open';
      r.reopen_count += 1;
      r.last_reopened_at = e.at;
      // The device that took it up owns it now, or resolveActiveRound would hand the round
      // back to a phone that is no longer doing the walking.
      r.device_id = e.device_id;
    }
  }
  return rounds;
}

/**
 * The round this device should resume, if any: the newest opening with no terminating event.
 * Scoped to the device because two operators legitimately sweep in parallel, and one must
 * never resume into the other's round.
 */
export function resolveActiveRound(
  events: readonly ClientEvent[],
  device_id: string,
): RoundSummary | null {
  let active: RoundSummary | null = null;
  for (const r of summariseRounds(events).values()) {
    if (r.status !== 'open' || r.device_id !== device_id || r.mode !== 'sweep') continue;
    if (!active || r.opened_at > active.opened_at) active = r;
  }
  return active;
}

/**
 * A correction left open on this device — where a scan goes while one is being recorded.
 *
 * Read from the log rather than held in the screen, so a killed app has still durably
 * written the scan it confirmed, and the next correction can tidy the opening away.
 */
export function resolveOpenCorrection(
  events: readonly ClientEvent[],
  device_id: string,
): RoundSummary | null {
  let open: RoundSummary | null = null;
  for (const r of summariseRounds(events).values()) {
    if (r.status !== 'open' || r.device_id !== device_id || r.mode !== 'correction') continue;
    if (!open || r.opened_at > open.opened_at) open = r;
  }
  return open;
}

/**
 * How long an opening is still taken to mean "someone is sweeping right now". A sweep lasts
 * minutes to hours; an opening from last week is an operator who walked away, and warning
 * about it for ever would train people to ignore the warning.
 */
export const OPEN_ROUND_TTL_HOURS = 12;

/**
 * How long a finished round can still be taken up again.
 *
 * Short on purpose. Reopening is for "I closed that a moment ago and I was wrong" — the
 * operator is still standing in the building, and the catalogue has not moved. Past that,
 * re-closing would diff the site against a catalogue that has changed since and declare
 * carts missing that were simply moved by someone else; recording the cart on its own
 * (a correction) stays the honest tool, and has no deadline.
 */
export const REOPEN_WINDOW_HOURS = 3;

/** When the round stopped being walked — the moment the window is counted from. */
export function roundEndedAt(r: RoundSummary): string | null {
  return r.closed_at ?? r.abandoned_at;
}

/**
 * Whether this round can still be taken up again. A correction is never "reopened": it
 * concluded nothing, so there is nothing to withdraw — record another one instead.
 */
export function canReopen(r: RoundSummary, now: Date = new Date()): boolean {
  if (r.mode !== 'sweep' || r.status === 'open') return false;
  const ended = roundEndedAt(r);
  if (!ended) return false;
  return now.getTime() - Date.parse(ended) <= REOPEN_WINDOW_HOURS * 3_600_000;
}

/**
 * Whether a scan can still be withdrawn. Same window, counted from the scan itself: a
 * sweep can legitimately run longer than the window, and a mis-scan noticed in the middle
 * of it must still be removable.
 */
export function canRetract(scannedAt: string, now: Date = new Date()): boolean {
  return now.getTime() - Date.parse(scannedAt) <= REOPEN_WINDOW_HOURS * 3_600_000;
}

/** Rounds open on *other* devices at this site — used to warn before opening a duplicate. */
export function otherOpenRoundsAtSite(
  events: readonly ClientEvent[],
  site_code: string,
  device_id: string,
  now: Date = new Date(),
): RoundSummary[] {
  const floor = now.getTime() - OPEN_ROUND_TTL_HOURS * 3_600_000;
  return [...summariseRounds(events).values()].filter(
    (r) =>
      r.status === 'open' &&
      r.mode === 'sweep' &&
      r.site_code === site_code &&
      r.device_id !== device_id &&
      Date.parse(r.opened_at) >= floor,
  );
}

/** Openings with no terminator, past the point of being a live sweep — worth an admin's
 *  attention because they were abandoned without saying so. */
export function stalledRounds(
  events: readonly ClientEvent[],
  now: Date = new Date(),
): RoundSummary[] {
  const floor = now.getTime() - OPEN_ROUND_TTL_HOURS * 3_600_000;
  return [...summariseRounds(events).values()]
    .filter((r) => r.status === 'open' && r.mode === 'sweep' && Date.parse(r.opened_at) < floor)
    .sort((a, b) => a.opened_at.localeCompare(b.opened_at));
}

export interface MissingComputation {
  expected: string[];
  scanned: string[];
  missing: string[];
}

/**
 * Expected-minus-seen, run on the device at closure so the operator gets the answer with no
 * network. `expected` is what this device believed was at the site, which is the honest
 * basis: it is what the operator could have gone looking for.
 */
export function computeMissing(
  states: ReadonlyMap<string, CartState>,
  site_code: string,
  scannedCodes: readonly string[],
): MissingComputation {
  const scanned = [...new Set(scannedCodes)].sort();
  const seen = new Set(scanned);
  const expected = cartsAtSite(states, site_code).map((s) => s.code);
  return { expected, scanned, missing: expected.filter((c) => !seen.has(c)) };
}

export interface RoundProgress {
  /** Carts recorded at this site **when the sweep started**. The stable denominator. */
  expected: string[];
  /** Everything scanned during the round. */
  scanned: string[];
  /** Scanned and expected: ticked off the list. */
  found: string[];
  /**
   * Scanned but *not* expected here — a cart that arrived from somewhere else, or one that
   * had never been located. Perfectly normal: any cart can be scanned at any site, and this
   * is how the system learns that it moved.
   */
  arrived: string[];
  /** Expected and not scanned. */
  missing: string[];
  /**
   * For each arrival, the site it was recorded at beforehand — or null if it had never been
   * located. Lets the screen say "arrivé de l'Entrepôt Nord" rather than just a count.
   */
  arrivedFrom: Record<string, string | null>;
}

/**
 * Progress of a sweep.
 *
 * `expected` is deliberately taken as of the round's *opening*, not of now. The projection
 * moves a cart the instant it is scanned, so an expected set computed from current state
 * grows as the operator scans carts in from other sites — the denominator would climb while
 * they work, and the "missing" figure at closure would not match the number they had been
 * watching count down. A snapshot at opening is stable and makes closure unsurprising.
 */
export function roundProgress(
  carts: readonly Cart[],
  events: readonly ClientEvent[],
  round: RoundSummary,
  opts: ProjectOptions = {},
): RoundProgress {
  const atOpen = events.filter((e) => e.at <= round.opened_at);
  const statesAtOpen = projectCarts(carts, atOpen, opts);
  const base = computeMissing(statesAtOpen, round.site_code, round.scanned);
  const expected = new Set(base.expected);
  const arrived = base.scanned.filter((c) => !expected.has(c));

  const arrivedFrom: Record<string, string | null> = {};
  for (const code of arrived) {
    arrivedFrom[code] = statesAtOpen.get(code)?.current_site ?? null;
  }

  return {
    ...base,
    found: base.scanned.filter((c) => expected.has(c)),
    arrived,
    arrivedFrom,
  };
}
